/** Sub-PR E2 §5.7 · AI 作業中狀態列。
 *
 * streaming 期間持續顯示（即使對話框已有文字），直到 message_stop/error。依 SSE 階段顯示：
 *   thinking（送出後未收事件）/ querying（收到 tool_use 未收 tool_result）/ replying（tool_result 後或 text_delta 中）。
 * 工具名以 display_name 顯示（非 list_my_events）；等待 > 10 秒加秒數。跳動圓點 + role=status/aria-live。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, keyframes } from '@mui/material';

export type WorkPhase = 'thinking' | 'querying' | 'replying';

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

interface Props {
  phase: WorkPhase;
  toolLabel?: string;   // querying 時的工具中文名（display_name）
}

const ChatWorkingStatus: React.FC<Props> = ({ phase, toolLabel }) => {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = React.useState(0);
  const startRef = React.useRef(Date.now());

  // phase 變化時重置計時（每個階段獨立計秒）
  React.useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [phase, toolLabel]);

  let text: string;
  if (phase === 'querying') {
    text = t('chat.status.querying', '正在查詢「{{tool}}」…', { tool: toolLabel || t('chat.status.tool', '工具') });
  } else if (phase === 'replying') {
    text = t('chat.status.replying', 'AI 回覆中…');
  } else {
    text = t('chat.status.thinking', 'AI 思考中…');
  }
  if (elapsed >= 10) {
    text += ' ' + t('chat.status.elapsed', '{{sec}} 秒', { sec: elapsed });
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, color: 'text.secondary' }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{
            width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main',
            animation: `${blink} 1.4s infinite both`, animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </Box>
      <Box component="span" sx={{ fontSize: 13 }}>{text}</Box>
    </Box>
  );
};

export default ChatWorkingStatus;
